import { Injectable, Renderer2, RendererFactory2, Inject } from '@angular/core';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
// Plugins
import { Subject } from 'rxjs';
import moment from 'moment';
// JSON
import languages from './json/languages.json';

@Injectable({
	providedIn: 'root'
})
export class HelperService {

	public spinner$ = new Subject<boolean>;
	private renderer: Renderer2;

	public constructor(
		private rendererFactory: RendererFactory2,
        @Inject(DOCUMENT) private document: Document
	) { }

	public objectToFormData<T, K = null>(object: T, fileName: K = null): FormData {
		let formData: FormData = new FormData();

		Object.keys(object).forEach((key: string) => {
			if (fileName && fileName[key]) {
				formData.append(key, object[key], fileName[key]);
			} else {
				formData.append(key, object[key]);
			}

		});

		return formData;
	}

	public regExpQuote(text: string): string {
		return text.replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1");
	}

	public passwordMatch(controlName: string, checkControlName: string): ValidatorFn {
        return (controls: AbstractControl) => {
            let control: AbstractControl = controls.get(controlName);
            let checkControl: AbstractControl = controls.get(checkControlName);

            if (checkControl.errors && !checkControl.errors['matching']) return null;

            if (control.value !== checkControl.value) {
                controls.get(checkControlName).setErrors({ matching: true });

                return { matching: true };
            }

            return null;
        };
    }

	public getDateWithLocalTime(timestamp: number): number {
		let date: string = moment(timestamp).format('YYYY-MM-DD');
		let time: string = moment().format('HH:mm:ss');

		return moment(`${ date } ${ time }`).valueOf();
	}

	public compareObjects(itemA: Object, itemB: Object): boolean {
        if (typeof (itemA) !== typeof (itemB)) return false;

        if (Object.keys(itemA)?.length !== Object.keys(itemB)?.length) return false;

        for (let key in itemA) {
            if (!itemB?.hasOwnProperty(key)) return false;

            if (itemA[key] == null || itemB[key] == null) continue;

            if (typeof itemA[key] != 'object' && typeof itemB[key] != 'object') continue;

            if (!this.compareObjects(itemA[key], itemB[key])) return false;
        }

        return true;
    }

	public appendScriptFile(scriptUrl: string, async: boolean = false, defer: boolean = false): void | boolean {
        if (this.document.querySelector(`[src="${ scriptUrl }"]`)) return false;

        this.renderer = this.rendererFactory.createRenderer(null, null);

        let script: HTMLScriptElement = this.renderer.createElement('script');

        script.src = scriptUrl;

        if (async) script.async;

        if (defer) script.defer;

        this.renderer.appendChild(this.document.body, script);
    }

	public getBase64(file: File): Promise<string | ArrayBuffer | null> {
		return 	new Promise((resolve, reject) => {
			let reader: FileReader = new FileReader();

			reader.readAsDataURL(file);

			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});
	}

	public getLanguages(): Array<{ code: string , name: string }> {
		return languages;
	}

	public capitalize(text: string): string {
        if (!text) return '';

        return text?.[0]?.toUpperCase() + text?.substring(1)?.toLowerCase();
    }

	public getUrlPattern(): RegExp {
		return /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
	}

	public emailControlValidation(): ValidatorFn {
		return (control: AbstractControl) => {
            if (control?.errors && !control?.errors?.['pattern']) return null;

			let email: string = control?.value?.trim();

			if (email) {
				// TLD up to 63 chars per DNS label; {2,4} wrongly rejected .solutions, .international, etc.
				let pattern: RegExp = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,63}$/i;

				if (!email?.match(pattern)) return { pattern: true };
			}

			let data: Array<string> = email?.toLowerCase()?.trim()?.split('.');

			if (
				(data?.[data?.length - 1] && data?.[data?.length - 2] && data?.[data?.length - 1] == data?.[data?.length - 2]) ||
				(email?.split('+')?.length > 2 )
			) {
				control?.setErrors({ pattern: true });
	
				return { pattern: true };
			}

            return null;
        };
	}
}